<template>
  <div class="lms-properties-map">
    <div ref="mapElement" class="lms-properties-map__canvas" />
    <div v-if="error" class="lms-properties-map__message">{{ error }}</div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  map: { type: Object, required: true },
  active: { type: Boolean, default: false }
})

const mapElement = ref(null)
const error = ref('')
let mapInstance = null
let cleanup = null
let initializedProvider = null

function providerReady () {
  if (props.map.provider === 'google') return !!window.google?.maps
  return !!window.L
}

function waitForProvider (attempt = 0) {
  if (!props.active || initializedProvider) return
  if (providerReady()) {
    initialize()
  } else if (attempt < 100) {
    window.setTimeout(() => waitForProvider(attempt + 1), 100)
  } else {
    error.value = 'The map could not be loaded.'
  }
}

function initialize () {
  if (!mapElement.value || !providerReady()) return
  error.value = ''
  if (props.map.provider === 'google') {
    initializeGoogle()
  } else {
    initializeLeaflet()
  }
}

function coordinate (value) {
  return Number(value) || 0
}

function pointsForGoogle (points) {
  return (points || []).map((point) => ({
    lat: coordinate(point[0]),
    lng: coordinate(point[1])
  }))
}

function pointsForLeaflet (points) {
  return (points || []).map((point) => [coordinate(point[0]), coordinate(point[1])])
}

function templateUrl (template, value) {
  return String(template || '').replace(':id', encodeURIComponent(value))
}

async function popupHtml (polygon) {
  const url = templateUrl(props.map.polygonPopupUrlTemplate, polygon.id)
  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    credentials: 'same-origin'
  })
  if (!response.ok) throw new Error('Unable to load property details.')
  const payload = await response.json()
  return payload.html || 'No details available.'
}

function drilldownUrl (level) {
  return String(props.map.drilldownUrlTemplate || '/prms/pub/properties?map_level=:map_level')
    .replace(':map_level', encodeURIComponent(level))
}

function addAirportControl (onSelect) {
  const airports = Object.entries(props.map.airports || {})
  if (airports.length < 2) return null

  const control = document.createElement('div')
  control.className = 'lms-properties-map__airports'
  airports.forEach(([code, airport]) => {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'lms-properties-map__airport'
    button.textContent = code
    button.addEventListener('click', () => onSelect(airport))
    control.appendChild(button)
  })
  return control
}

function initializeGoogle () {
  const google = window.google
  const center = {
    lat: coordinate(props.map.center?.[0]),
    lng: coordinate(props.map.center?.[1])
  }
  const googleMap = new google.maps.Map(mapElement.value, {
    center,
    zoom: Number(props.map.zoom) || 0,
    mapTypeId: google.maps.MapTypeId.SATELLITE,
    keyboardShortcuts: false,
    scrollwheel: false,
    streetViewControl: false,
    disableDoubleClickZoom: false,
    zoomControl: true,
    minZoom: Number(props.map.zoom) || 0
  })
  const polygons = []
  const listeners = []
  const infoWindow = new google.maps.InfoWindow()
  let selected = null

  const selectAirport = (airport) => {
    googleMap.setCenter({
      lat: coordinate(airport.lat),
      lng: coordinate(airport.lng)
    })
    googleMap.setZoom(Number(airport.gMapZoom) || googleMap.getZoom())
  }
  const airportControl = addAirportControl(selectAirport)
  if (airportControl) googleMap.controls[google.maps.ControlPosition.BOTTOM_LEFT].push(airportControl)

  ;(props.map.polygons || []).forEach((data) => {
    const polygon = new google.maps.Polygon({
      paths: pointsForGoogle(data.points),
      strokeColor: data.color,
      strokeOpacity: 0.8,
      strokeWeight: 1.5,
      fillColor: data.color,
      fillOpacity: 0.6
    })
    polygon.setMap(googleMap)
    polygon.addListener('click', async () => {
      if (selected === data.id) return
      selected = data.id
      infoWindow.setContent('Loading Data…')
      infoWindow.setPosition(polygonCenterGoogle(polygon, google))
      infoWindow.open(googleMap)
      try {
        infoWindow.setContent(await popupHtml(data))
      } catch (e) {
        infoWindow.setContent(e.message)
      }
    })
    polygon.addListener('mouseover', () => polygon.setOptions({ fillOpacity: 0.8 }))
    polygon.addListener('mouseout', () => polygon.setOptions({ fillOpacity: 0.6 }))
    polygons.push(polygon)
  })

  Object.entries(props.map.drilldowns || {}).forEach(([level, data]) => {
    const polygon = new google.maps.Polygon({
      paths: pointsForGoogle(data.points),
      strokeColor: data.color,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: data.color,
      fillOpacity: 0.35
    })
    polygon.setMap(googleMap)
    listeners.push(
      polygon.addListener('click', () => { window.location.href = drilldownUrl(level) }),
      polygon.addListener('mouseover', () => polygon.setOptions({ fillOpacity: 0.55 })),
      polygon.addListener('mouseout', () => polygon.setOptions({ fillOpacity: 0.35 }))
    )
    polygons.push(polygon)
  })

  mapInstance = googleMap
  initializedProvider = 'google'
  cleanup = () => {
    infoWindow.close()
    polygons.forEach((polygon) => polygon.setMap(null))
    listeners.forEach((listener) => listener.remove())
    if (airportControl) airportControl.remove()
    mapInstance = null
  }
}

function polygonCenterGoogle (polygon, google) {
  const bounds = new google.maps.LatLngBounds()
  polygon.getPath().forEach((point) => bounds.extend(point))
  return bounds.getCenter()
}

function initializeLeaflet () {
  const L = window.L
  const overlays = Object.entries(props.map.overlays || {})
  const imageOverlays = overlays.filter(([, overlay]) => overlay?.src)
  const leafletMap = L.map(mapElement.value, imageOverlays.length
    ? { crs: L.CRS.Simple, minZoom: -1 }
    : { minZoom: Number(props.map.zoom) || 0 }).setView(
      [coordinate(props.map.center?.[0]), coordinate(props.map.center?.[1])],
      Number(props.map.zoom) || 0
    )
  const layers = {}
  const polygonLayers = []

  if (imageOverlays.length) {
    imageOverlays.forEach(([name, overlay]) => {
      const bounds = [[0, 0], overlay.bounds]
      const layer = L.layerGroup()
      L.imageOverlay(overlay.src, bounds).addTo(layer)
      layer.addTo(leafletMap)
      layers[name] = layer
    })
    const firstOverlay = imageOverlays[0][1]
    leafletMap.fitBounds([[0, 0], firstOverlay.bounds])
  } else {
    const layer = L.layerGroup().addTo(leafletMap)
    layers.Satellite = layer
    const tileUrl = window.SATELLITE_URL ||
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
    L.tileLayer(tileUrl, {
      attribution: window.SATELLITE_ATTRIBUTION || ''
    }).addTo(layer)
  }

  const selectAirport = (airport) => {
    leafletMap.flyTo(
      [coordinate(airport.lat), coordinate(airport.lng)],
      Number(airport.gMapZoom) || leafletMap.getZoom()
    )
  }
  const airportControl = addAirportControl(selectAirport)
  if (airportControl) {
    const control = L.control({ position: 'bottomleft' })
    control.onAdd = () => airportControl
    control.addTo(leafletMap)
  }

  const addPolygon = (data, options = {}) => {
    const layer = L.polygon(pointsForLeaflet(data.points), {
      weight: options.weight || 1,
      color: data.color,
      fillColor: data.color,
      fillOpacity: options.fillOpacity || 0.6
    }).addTo(layers[data.map_type] || layers[Object.keys(layers)[0]])
    layer.bindTooltip(data.title)
    layer.on('click', async () => {
      if (options.drilldown) {
        window.location.href = drilldownUrl(options.drilldown)
        return
      }
      layer.bindPopup('Loading Data…').openPopup()
      try {
        layer.bindPopup(await popupHtml(data)).openPopup()
      } catch (e) {
        layer.bindPopup(e.message).openPopup()
      }
    })
    polygonLayers.push(layer)
  }

  ;(props.map.polygons || []).forEach((data) => addPolygon(data))
  Object.entries(props.map.drilldowns || {}).forEach(([level, data]) => {
    addPolygon(data, { drilldown: level, weight: 2, fillOpacity: 0.35 })
  })

  mapInstance = leafletMap
  initializedProvider = 'leaflet'
  cleanup = () => {
    polygonLayers.forEach((polygon) => polygon.remove())
    if (airportControl) airportControl.remove()
    leafletMap.remove()
    mapInstance = null
  }
}

function resizeMap () {
  if (!mapInstance) return
  if (props.map.provider === 'google') {
    window.google.maps.event.trigger(mapInstance, 'resize')
  } else {
    mapInstance.invalidateSize()
  }
}

watch(() => props.active, async (active) => {
  if (!active) return
  await nextTick()
  if (!initializedProvider) waitForProvider()
  window.requestAnimationFrame(resizeMap)
})

onMounted(() => {
  if (props.active) waitForProvider()
})

onBeforeUnmount(() => {
  if (cleanup) cleanup()
})
</script>

<style>
.lms-properties-map {
  position: relative;
  min-height: 500px;
}

.lms-properties-map__canvas {
  width: 100%;
  height: 500px;
  border: 1px solid var(--lms-border);
  border-radius: 12px;
  overflow: hidden;
}

.lms-properties-map__message {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: var(--lms-text-secondary);
  pointer-events: none;
}

.lms-properties-map__airports {
  display: flex;
  gap: 4px;
  margin: 0 0 12px 12px;
  padding: 5px;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 4px rgb(0 0 0 / 30%);
}

.lms-properties-map__airport {
  border: 0;
  border-radius: 4px;
  padding: 5px 8px;
  background: #fff;
  color: #1f5266;
  cursor: pointer;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 700;
}

.lms-properties-map__airport:hover {
  background: #e6f3f7;
}
</style>
